import { useRef, useState } from 'react';
import Breadcrumb from '@/components/base/Breadcrumb';
import { usePageMeta } from '@/hooks/usePageMeta';

const FORM_ADDR = 'https://readdy.ai/api/form/db784draskivmpnrchcg';

export default function About() {
  usePageMeta({
    title: '关于我们 - 河北彩花影视大全',
    description:
      '了解河北彩花影视大全的网站定位、使用须知与联系方式，本站专注影视作品与明星资料的展示。',
    keywords: '关于我们,河北彩花影视大全,网站介绍',
  });

  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    setStatus('submitting');
    setFormError('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(FORM_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';

      if (response.ok && parsed?.code === 'OK' && !serverMsg.toLowerCase().includes('spam')) {
        setStatus('success');
        formRef.current?.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后再试。');
        setStatus('error');
      }
    } catch {
      setFormError('网络异常，请检查网络后重试。');
      setStatus('error');
    }
  };

  return (
    <div className="w-full">
      <div className="w-full border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-5xl px-4 py-8 md:px-6 md:py-10">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于我们 - 河北彩花影视大全
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个专注影视作品与明星资料的内容站，帮助影视爱好者与粉丝一站式了解作品详情、人物档案与最新动态。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-5xl px-4 py-10 md:px-6 md:py-14">
        <div className="grid gap-10 lg:grid-cols-3">
          <div className="flex flex-col gap-8 lg:col-span-2">
            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950">网站定位</h2>
              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <p className="text-sm leading-loose text-foreground-700">
                  河北彩花影视大全聚焦河北彩花的影视作品及个人资料，目标用户为影视爱好者、粉丝以及希望获取河北彩花最新影视动态的人群。
                  站内提供参演电影与电视剧的信息查询、作品详情浏览以及相关资讯，满足用户对影视作品与明星资料的综合了解需求。
                </p>
                <p className="mt-4 text-sm leading-loose text-foreground-700">
                  我们希望通过清晰的信息结构、便捷的检索方式与舒适的阅读体验，让每一次查阅都高效而愉快。
                </p>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950">使用须知</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className="ri-database-2-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">数据为示范内容</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    站内作品、明星与资讯均为示例数据，用于展示信息结构，不代表真实公开信息。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-bookmark-3-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">收藏保存在本地</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    收藏功能仅保存在当前浏览器本地，用于演示效果，清除浏览器数据后会重置。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                    <i className="ri-share-forward-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">分享为前端演示</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    分享按钮会尝试调用系统分享或复制当前页面链接，不涉及任何后台数据。
                  </p>
                </div>
                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                    <i className="ri-shield-check-line text-xl"></i>
                  </div>
                  <h3 className="mt-3 text-sm font-semibold text-foreground-950">仅供学习交流</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
                    本站内容仅用于学习交流与产品演示，请勿用于任何商业用途。
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="font-heading text-lg font-bold text-foreground-950">联系方式</h2>
              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-10">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">反馈邮箱</p>
                      <p className="text-sm font-medium text-foreground-900">feedback@hbh-movie-demo.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-question-answer-line text-lg"></i>
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">反馈处理</p>
                      <p className="text-sm font-medium text-foreground-900">1-3 个工作日内回复</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>

          <aside className="lg:col-span-1">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6">
              <h2 className="font-heading text-lg font-bold text-foreground-950">留言反馈</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                有任何建议或问题，欢迎通过下面的表单告诉我们。
              </p>

              <form
                ref={formRef}
                id="feedback"
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-5 flex flex-col gap-4"
              >
                <input
                  type="text"
                  name="website_alt"
                  className="contact-field-alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
                <div>
                  <label htmlFor="fb-name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    您的称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="fb-message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="fb-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="最多 500 字"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' ? '提交中…' : '提交反馈'}
                </button>

                {status === 'success' && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-50 px-3.5 py-2.5 text-sm text-primary-700">
                    <i className="ri-checkbox-circle-line"></i>
                    提交成功，感谢您的反馈！
                  </p>
                )}
                {status === 'error' && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 px-3.5 py-2.5 text-sm text-accent-900">
                    <i className="ri-error-warning-line"></i>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}